import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import ProductList from '../../shared/common/ProductList';
import FilterBar from '../../shared/common/FilterBar';
import { RESERVE_DETAILS_SORT_BY } from '../../shared/common/SortByDropDown';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import Pagination from '../../shared/common/Pagination';
import ModalDialog from '../../shared/components/modal/ModalDialog';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET, PATH_ORDERS } from '../../store/constants';
import { TYPE_WARNING } from '../../shared/notification/actions';
import NoElementsFound, { NO_ELEMENTS_RESERVES } from '../../shared/components/NoElementsFound';
import { ContainerInnerWithColumns } from '../../shared/common/ContainerInner';
import { FilterAndProductListWrapper } from '../../shared/common/FilterAndProductListWrapper';
import { ReserveDetailSettings } from './components/ReserveDetailSettings';
import ReserveDetailHeader from './components/ReserveDetailHeader';
import { RESERVE_STATUS_CLOSED } from './constants';
import ReserveListItem from './components/ReserveListItem';
import { useParams } from 'react-router';
import { useReserveReportSummary } from './api/reserve-list/useReserveReportSummary';
import { useReserveReportProducts } from './api/reserve-list/useReserveReportProducts';
import { useCopyAmountChange } from './api/reserve-list-item/useCopyAmountChange';
import { useFilterAcceptReject } from './api/reserve-list-item/useFilterAcceptReject';
import { useReopenReserveList } from './api/reserve-list/useReopenReserveList';
import { useGenerateReserveReportMutation } from './api/reserve-report/useGenerateReserveReportMutation';
import { useSendForApproval } from '../../api/reserveLists/useSendForApproval';
import { ProductMetaData, ProductSorting } from '../../../models/Product';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import { fetchReserveListDetailAggregationsQuickSearch } from '../../api/reserveLists/fetchReserveListDetailAggregationsQuickSearch';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchReserveListDetailAggregations } from '../../api/reserveLists/fetchReserveListDetailAggregations';
import { QueryKeyBuilder } from '../../shared/filter/useAggregationsQuery';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { useDefaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { useTranslation } from 'react-i18next';
import { Article } from '../../../models/Article';
import { useDisplayFilterSearchParam } from '../../shared/utils/useDisplayFilterSearchParam';
import { LoanFormatState } from '../../app-store/loanFormat/loanFormatReducer';
import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EAUDIOBOOKS,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EBOOKS
} from '../../shared/loanFormat/constants';
import { DISPLAY_TYPE_LIST } from '../../shared/common/displayType/types';
import { showNotification } from '../../shared/notification/toastNotification';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../shared/filter/useProductFilters';
import isEmpty from 'lodash/isEmpty';
import { useNavigate } from 'react-router-dom';

const IncludeAllIcon = styled.i`
    && {
        && {
            color: #7fc31c;
        }
    }
`;

const ExcludeAllIcon = styled.i`
    && {
        && {
            color: #ff0c00;
        }
    }
`;

const IncludeAllButton = styled.button`
    :hover {
        i {
            color: inherit !important;
        }
    }
`;

export const ReserveDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { reserveListId = '' } = useParams<'reserveListId'>();

    const [showReopenDialog, setShowReopenDialog] = useState(false);
    const [showOutdatedWarning, setShowOutdatedWarning] = useState(false);
    const [outdatedWarningWasShown, setOutdatedWarningWasShown] = useState(false);

    const { data: reserveReportSummary } = useReserveReportSummary(reserveListId);
    const { mutate: generateReserveReport } = useGenerateReserveReportMutation();

    const isClosedState = reserveReportSummary?.status === RESERVE_STATUS_CLOSED;
    const isOrderedState = !isNil(reserveReportSummary?.orderNumber);

    const {
        mutateAsync: sendForApproval,
        isLoading: isLoadingSendForApproval,
        errorMessage: sendForApprovalErrorMessage
    } = useSendForApproval({ listId: reserveListId });

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, sortBy, pageSize } = usePaginationAndSortParams({
        defaultPageSize: 45,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const { productFilter } = useProductFilters();

    const { data: productData, isLoading: isProductsLoading } = useReserveReportProducts({
        reserveListId,
        filterMap: productFilter,
        pagination: {
            page,
            pageSize,
            sortBy
        }
    });

    const { mutateAsync: changeCopyAmount } = useCopyAmountChange(reserveListId);
    const { mutate: doReopen } = useReopenReserveList(reserveListId);
    const { mutate: filterAcceptReject, isLoading: isFilterAcceptLoading } = useFilterAcceptReject(
        reserveListId,
        productFilter,
        {
            page,
            pageSize,
            sortBy
        }
    );

    const products = productData?.hits ?? [];
    const totalProducts = productData?.totalHits ?? 0;

    const defaultVisibleFilterGroups = useDefaultVisibleFilterGroups();

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => [
            ...defaultVisibleFilterGroups.filter(
                item => !['releaseFrequency', 'releaseQuantity', 'mucPrice'].includes(item)
            ),
            // reserve report uses the legacy (non-MUC) price filter "price" instead of "mucPrice"
            'price'
        ],
        [defaultVisibleFilterGroups]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => {
            return ['reserveDetailPage', reserveListId, 'aggregations', filters, expandedFilterGroups];
        },
        [reserveListId]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchReserveListDetailAggregations({ ...options, reserveListId }),
        [reserveListId]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options => fetchReserveListDetailAggregationsQuickSearch({ ...options, reserveListId }),
        [reserveListId]
    );
    //endregion

    useEffect(() => {
        if (reserveReportSummary?.outdatedDueToOrder && !outdatedWarningWasShown) {
            setShowOutdatedWarning(true);
            setOutdatedWarningWasShown(true);
        }
    }, [outdatedWarningWasShown, reserveReportSummary?.outdatedDueToOrder]);

    const closeRegenerateModal = useCallback(() => {
        setShowOutdatedWarning(false);
    }, []);

    const acceptRegeneration = useCallback(() => {
        setShowOutdatedWarning(false);

        const generateSettings = {
            generatedWithDefaults: reserveReportSummary?.generatedWithDefaults,
            loanFormatDefaults: reserveReportSummary?.loanFormatSettings
        };

        generateReserveReport({ generateSettings });
    }, [generateReserveReport, reserveReportSummary?.generatedWithDefaults, reserveReportSummary?.loanFormatSettings]);

    const onCopyAmountChange = useCallback(
        async (
            product: ProductMetaData & Article,
            amount: number,
            command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
        ) => {
            await changeCopyAmount({ productId: product.id, numCopies: amount, command });
        },
        [changeCopyAmount]
    );

    const reopenCancel = useCallback(() => {
        setShowReopenDialog(false);
    }, []);

    const reopen = useCallback(() => {
        doReopen();
        setShowReopenDialog(false);
    }, [doReopen]);

    const eaudiobooksAmount = reserveReportSummary?.summary?.eaudiobooksAmount ?? 0;
    const ebooksAmount = reserveReportSummary?.summary?.ebooksAmount ?? 0;

    const proceedToCheckout = useCallback(() => {
        if (eaudiobooksAmount > 0 || ebooksAmount > 0) {
            navigate('checkout');
        } else {
            showNotification(t('notifications.reserveDetail.proceedToCheckOut.warning'), TYPE_WARNING);
        }
    }, [eaudiobooksAmount, ebooksAmount, navigate, t]);

    const proceedToOrder = useCallback(() => {
        if (!reserveReportSummary?.orderNumber) {
            showNotification(t('notifications.reserveDetail.proceedToOrder.warning'), TYPE_WARNING);
        } else {
            navigate(`${PATH_ORDERS}/${reserveReportSummary?.orderNumber}`);
        }
    }, [navigate, reserveReportSummary?.orderNumber, t]);

    const proceedToReopen = useCallback(() => {
        if (reserveReportSummary?.outdatedDueToGeneration) {
            setShowReopenDialog(true);
        } else {
            reopen();
        }
    }, [reopen, reserveReportSummary?.outdatedDueToGeneration]);

    const reportHasEbooks = reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EBOOKS];
    const reportHasEaudiobooks = reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EAUDIOBOOKS];

    const ebookReserveRatio = reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EBOOKS]?.reserveRatio ?? 0;
    const eaudiobookReserveRatio =
        reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EAUDIOBOOKS]?.reserveRatio ?? 0;

    let reserveLoanFormat: LoanFormatState | null = null;
    if (reportHasEbooks && !reportHasEaudiobooks) {
        reserveLoanFormat = LOANFORMAT_EBOOK_OBJECT;
    } else if (!reportHasEbooks && reportHasEaudiobooks) {
        reserveLoanFormat = LOANFORMAT_EAUDIOBOOK_OBJECT;
    }

    const isAcceptRejectAllDisabled = isClosedState || isOrderedState || totalProducts <= 0 || isProductsLoading;

    return (
        <ContainerInnerWithColumns className={classNames({ 'min-h-screen': isProductsLoading }, 'reserve-detail-list')}>
            <LoadingWrapper>
                <LoadingOverlay loading={isProductsLoading} />
            </LoadingWrapper>
            <ModalDialog show={showReopenDialog} onCloseButtonClick={reopenCancel} title="Re-open">
                <p>{t('reserveReports.reserveReport.viewReport.reopenDialog')}</p>
                <div className="dlb-actions">
                    <a className="lk" onClick={reopenCancel}>
                        {t('cancel')}
                    </a>
                    <a className="lk lk-primary" onClick={reopen}>
                        {t('reserveReports.reserveReport.viewReport.reopen')}
                    </a>
                </div>
            </ModalDialog>
            <ModalDialog show={showOutdatedWarning} onCloseButtonClick={closeRegenerateModal} title="Report Outdated">
                <p>{t('reserveReports.reserveReport.viewReport.outDatedDialog')}</p>
                <div className="dlb-actions">
                    <a className="lk" onClick={closeRegenerateModal}>
                        {t('reserveReports.reserveReport.viewReport.dontRegenerate')}
                    </a>
                    <a className="lk lk-primary" onClick={acceptRegeneration}>
                        {t('reserveReports.reserveReport.viewReport.regenerate')}
                    </a>
                </div>
            </ModalDialog>

            <ReserveDetailHeader
                isOrdered={isOrderedState}
                proceedToCheckout={proceedToCheckout}
                proceedToOrder={proceedToOrder}
                proceedToReopen={proceedToReopen}
                reserveReportDetails={reserveReportSummary}
                loanFormat={reserveLoanFormat}
                sendForApproval={sendForApproval}
                isLoadingSendForApproval={isLoadingSendForApproval}
                sendForApprovalError={sendForApprovalErrorMessage}
            />

            <ReserveDetailSettings
                reserveReportDetails={reserveReportSummary}
                ebookReserveRatio={ebookReserveRatio}
                eAudiobookReserveRatio={eaudiobookReserveRatio}
            />

            {products.length > 0 ? (
                <div className="reserve-list-control-bar">
                    <div className="reserve-list-filterbar-wrapper">
                        <FilterBar
                            sortBy={sortBy}
                            sorting={RESERVE_DETAILS_SORT_BY}
                            hideGroupBy={true}
                            hideDisplayType={true}
                            hideSelectButtons={true}
                            totalProducts={totalProducts}
                            pageSize={pageSize}
                            isFilterToggleReplaced={isOrderedState}
                            leftColumnText={'Items'}
                        />
                    </div>
                    <div className="reserve-action-buttons" data-displayname="ReserveActionButtons">
                        {/* We have to overwrite display here because it is defined in material link and bt classes */}
                        <IncludeAllButton
                            disabled={isFilterAcceptLoading}
                            className={classNames('bt', 'bt-bordered', 'material-link', {
                                disabled: isAcceptRejectAllDisabled || isFilterAcceptLoading
                            })}
                            onClick={() => filterAcceptReject('accept')}
                            style={{ display: 'inline-flex' }}
                        >
                            <IncludeAllIcon className="material-icons">done</IncludeAllIcon>
                            {!isEmpty(productFilter) ? (
                                <span>{t('reserveReports.reserveReport.viewReport.includeAllFiltered')}</span>
                            ) : (
                                <span>{t('reserveReports.reserveReport.viewReport.includeAll')}</span>
                            )}
                        </IncludeAllButton>
                        <IncludeAllButton
                            disabled={isFilterAcceptLoading}
                            className={classNames('bt', 'bt-bordered', 'negative', 'material-link', {
                                disabled: isAcceptRejectAllDisabled || isFilterAcceptLoading
                            })}
                            onClick={() => filterAcceptReject('reject')}
                            style={{ display: 'inline-flex' }}
                        >
                            <ExcludeAllIcon className="material-icons">clear</ExcludeAllIcon>
                            {!isEmpty(productFilter) ? (
                                <span>{t('reserveReports.reserveReport.viewReport.excludeAllFiltered')}</span>
                            ) : (
                                <span>{t('reserveReports.reserveReport.viewReport.excludeAll')}</span>
                            )}
                        </IncludeAllButton>
                    </div>
                </div>
            ) : null}

            <FilterAndProductListWrapper>
                {displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={visibleFilterGroups}
                        buildQueryKey={aggregationsQueryKeyBuilder}
                        fetchAggregations={fetchAggregations}
                        fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                    />
                ) : null}
                {products.length > 0 ? (
                    <div className="ca-main">
                        <ProductList products={products} displayType={DISPLAY_TYPE_LIST} onAddPrice={async () => {}}>
                            {({ key, isListView, ...value }) => (
                                <ReserveListItem
                                    {...value}
                                    key={key}
                                    reserveListId={reserveListId}
                                    readonly={isClosedState || isOrderedState}
                                    hideAcceptReject={isClosedState || isOrderedState || isFilterAcceptLoading}
                                    onCopyAmountChange={(amount, command) =>
                                        onCopyAmountChange(value.product, amount, command)
                                    }
                                />
                            )}
                        </ProductList>
                        <div style={{ marginBottom: '100px' }}>
                            <Pagination totalItems={totalProducts} pageSize={pageSize} />
                        </div>
                    </div>
                ) : (
                    <NoElementsFound type={NO_ELEMENTS_RESERVES} />
                )}
            </FilterAndProductListWrapper>
        </ContainerInnerWithColumns>
    );
};
